import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Card, CardHeader, CardContent } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import QuestionView from './QuestionView';
import { useFormContext } from '../../../hooks/useForm';
import { reorderArray } from '../../../utils/filterData';
import TextInput from '../../../components/FormInputs/TextInput';

SectionPanel.propTypes = {
  section: PropTypes.object,
  sectionsList: PropTypes.array,
  page: PropTypes.number,
  setSections: PropTypes.func,
  setPage: PropTypes.func,
  setHasSelectedSection: PropTypes.func,
  hasSelectedSection: PropTypes.bool,
};

export default function SectionPanel({
  section,
  sectionsList,
  page,
  setSections,
  setPage,
  setHasSelectedSection,
  hasSelectedSection,
}) {
  const { getSectionQuestions, updateFormData, questions } = useFormContext();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [sectionQuestions, setSectionQuestions] = useState(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    const questionList = getSectionQuestions(section.id);
    setSectionQuestions(questionList);
  }, [getSectionQuestions, section.id]);

  /**
   * SYNCHRONOUS branching check — reads directly from context `questions` object,
   * same pattern as FormSection.js. No useEffect delay, so the guard below is
   * always accurate on the very first render, preventing the static nextSection
   * effect from overriding QuestionView's dynamic reorder.
   */
  const isBranchingActive = useMemo(() => {
    const questionIds = section?.questions || [];
    return questionIds.some((qId) => questions[qId]?.redirectOnAnswer === true);
  }, [section, questions]);

  /**
   * STATIC NAVIGATION OBSERVER
   * Only fires when no question in this section drives dynamic branching.
   * If branching is active, QuestionView owns all navigation for this section.
   */
  useEffect(() => {
    if (section.nextSection === 'submit') {
      setHasSelectedSection(false);
      return;
    }
    if (isBranchingActive) return;

    if (section.nextSection && section.nextSection !== 'submit') {
      const nextIndex = sectionsList.findIndex((s) => s.id === section.nextSection);
      if (nextIndex !== -1) {
        const newSectionOrder = reorderArray(sectionsList, page, nextIndex);
        setSections(newSectionOrder.array);
        setPage(newSectionOrder.currentIndex);
        setHasSelectedSection(true);
      }
    }
  }, [
    section,
    sectionsList,
    page,
    setSections,
    setPage,
    setHasSelectedSection,
    isBranchingActive,
  ]);

  const setTicketDescription = (value) => {
    setDescription(value);
    updateFormData({ ticketDescription: value });
  };

  return (
    <Card sx={{ mt: 2, boxShadow: 14, borderRadius: '24px' }}>
      <CardHeader
        title={`${t('pages.formPage.section')} ${section.name || ''}`}
        sx={{
          bgcolor: 'rgba(145, 158, 171, 0.04)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          '& .MuiCardHeader-title': {
            fontSize: isSmallScreen ? '0.775rem' : '1.1rem',
            fontWeight: 700,
          },
        }}
      />
      <CardContent>
        {sectionQuestions?.map((question) => (
          <QuestionView
            key={`q${question.id}`}
            question={question}
            sectionsList={sectionsList}
            page={page}
            setSections={setSections}
            setPage={setPage}
            setHasSelectedSection={setHasSelectedSection}
            section={section}
          />
        ))}

        {section.nextSection === 'submit' && !hasSelectedSection && (
          <TextInput
            fullWidth
            multiline
            rows={3}
            defaultValue={description}
            label={t('pages.formPage.additonalDetail')}
            sx={{ marginTop: '2vh' }}
            onBlur={(e) => setTicketDescription(e.target.value)}
            size={isSmallScreen ? 'small' : 'medium'}
          />
        )}
      </CardContent>
    </Card>
  );
}
